import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import { useAssets, useCreateAsset, useReorderAssets } from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';
import { MoveButtons, reorder } from '../../components/MoveButtons.js';
import { ASSET_TYPE_OPTIONS, assetTypeIcon, assetTypeLabel } from '../../lib/asset-types.js';
import type { Asset, AssetType } from '@keetcheck/shared';

export function AssetsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const { data: assets = [], isLoading } = useAssets(orgId);
  const createMut = useCreateAsset(orgId);
  const reorderMut = useReorderAssets(orgId);
  const move = (i: number, dir: -1 | 1) => {
    const order = reorder(assets, i, dir);
    if (order.length) reorderMut.mutate(order);
  };

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [assetType, setAssetType] = useState<AssetType>('truck');
  const [registration, setRegistration] = useState('');

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createMut.mutate(
      { name: name.trim(), assetType, registration: registration.trim() || undefined },
      {
        onSuccess: () => {
          setName(''); setAssetType('truck'); setRegistration('');
          setShowForm(false);
        },
      },
    );
  }

  return (
    <div style={{ background: theme.surface, flex: 1 }}>
      {/* toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: `1px solid ${theme.border}`,
        background: theme.bg,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.textDim }}>
          {assets.length} asset{assets.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setShowForm(!showForm)}
          style={addBtn}
        >
          <Icon name="plus" size={15} />
          Add asset
        </button>
      </div>

      {/* create form */}
      {showForm && (
        <form onSubmit={handleCreate} style={{
          padding: '14px 16px',
          background: theme.surface2,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <input
            required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Asset name (e.g. Rescue 1)"
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <select
              value={assetType} onChange={(e) => setAssetType(e.target.value as AssetType)}
              aria-label="Type" style={{ ...inputStyle, flex: 1 }}
            >
              {ASSET_TYPE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <input
              value={registration} onChange={(e) => setRegistration(e.target.value)}
              placeholder="Rego / serial"
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => setShowForm(false)} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={createMut.isPending} style={submitBtnStyle}>
              {createMut.isPending ? 'Creating…' : 'Create asset'}
            </button>
          </div>
        </form>
      )}

      {/* list */}
      {isLoading && <EmptyMsg>Loading…</EmptyMsg>}
      {!isLoading && assets.length === 0 && <EmptyMsg>No assets yet — create one above.</EmptyMsg>}
      {assets.map((a, i) => (
        <AssetRow
          key={a.id} asset={a}
          last={i === assets.length - 1}
          onClick={() => nav(`/admin/assets/${a.id}`)}
          move={
            <MoveButtons
              disabled={reorderMut.isPending}
              onUp={i > 0 ? () => move(i, -1) : undefined}
              onDown={i < assets.length - 1 ? () => move(i, 1) : undefined}
            />
          }
        />
      ))}
    </div>
  );
}

function AssetRow({ asset, last, onClick, move }: {
  asset: Asset; last: boolean; onClick: () => void; move: React.ReactNode;
}) {
  const sectionCount = asset.sectionCount ?? 0;
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px',
        borderBottom: last ? 'none' : `1px solid ${theme.border}`,
        cursor: 'pointer',
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 8, flexShrink: 0,
        background: theme.surface2, border: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: theme.textMuted,
      }}>
        <Icon name={assetTypeIcon(asset.assetType)} size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>
          {asset.name}
          {asset.primaryResponse && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: theme.status.service.bg, color: theme.status.service.fg }}>Primary</span>}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
          {[assetTypeLabel(asset.assetType), asset.registration, sectionCount > 0 ? `${sectionCount} section${sectionCount !== 1 ? 's' : ''}` : null]
            .filter(Boolean).join(' · ')}
        </div>
      </div>
      {move}
      <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
    </div>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>{children}</div>;
}

const inputStyle: React.CSSProperties = {
  padding: '10px 12px', background: theme.surface,
  border: `1px solid ${theme.border}`, borderRadius: 10,
  fontSize: 14, color: theme.text, fontFamily: theme.font,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};

const addBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const cancelBtn: React.CSSProperties = {
  flex: 1, padding: '10px 0', borderRadius: 10,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const submitBtnStyle: React.CSSProperties = {
  flex: 2, padding: '10px 0', borderRadius: 10,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font,
};
